// DEPENDENCIES ---------------------------------------------------------------- //

import { useState } from 'react';

import { css } from '@emotion/css';
import { MdExpandMore } from 'react-icons/md';
import { Collapse, IconButton, LinearProgress, Paper, Typography } from '@mui/material';

import { useProgramCoverageQuote } from '@features/moveCreation/hooks/useProgramCoverageQuote';
import { buildProgramCoverageLines } from '@features/moveCreation/lib/programCoverageLines';
import { eligibilityView } from '@features/moveCreation/lib/programVehicleEligibility';
import { EligibilityStatusLine } from '@features/moveCreation/components/ProgramEligibilityParts';
import { CoverageDetails, CoverageHeadline } from '@features/moveCreation/components/ProgramCoverageParts';

// COMPONENT ---------------------------------------------------------------- //

/**
 * The program coverage summary at the end of the planner form, just above the
 * submit, so a dealer who has scrolled down sees what they may owe (or be
 * reimbursed) and whether the move qualifies without scrolling back to the
 * pinned bar. Collapsed it carries the same headline figure and eligibility
 * verdict as that bar; expanded, the same money breakdown and every rule of
 * the contract, so a dealer who would pay the ineligible figure sees which
 * entry to fix ("Genesi") before creating the move.
 *
 * Same gates as the bar: renders nothing unless the customer's
 * `program_visuals` flag is on and there is a quote (enrolled, with a lane),
 * and shows no money until the vehicle is in. Informational only.
 */
const MovePlannerProgramEligibility = () => {
  const cls = useStyles();
  const [expanded, setExpanded] = useState(false);

  const { quote, loading, visuals, vehicleReady, missingVehicleFacts, vehicle, vehicleLabel, distanceMiles } =
    useProgramCoverageQuote();
  const lines = buildProgramCoverageLines(quote, visuals);
  if (!quote || !lines) return null;
  const view = eligibilityView(quote, vehicle, missingVehicleFacts);
  // Until the vehicle is in, the VIN decode can still flip what the dealer
  // pays, so the box judges the entry but names no figure, as the bar does.
  const money = vehicleReady ? lines : null;

  return (
    <Paper variant='custom' sx={{ padding: 0, marginTop: `24px`, overflow: 'hidden' }} data-testid='program-eligibility'>
      <div
        className={cls.bar}
        onClick={() => setExpanded(v => !v)}
        role='button'
        aria-expanded={expanded}
        aria-label='Toggle program coverage summary details'
      >
        <Typography sx={{ fontWeight: 600, fontSize: `14px`, whiteSpace: 'nowrap' }}>Program Coverage</Typography>
        <div className={cls.headline}>{money ? <CoverageHeadline lines={money} /> : null}</div>
        <IconButton size='small' sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
          <MdExpandMore size={20} />
        </IconButton>
      </div>

      {/* Outside the collapse, like the bar's: a dealer paying the ineligible figure has
          to see why. It judges the entry the last quote saw, so it waits for the one in flight. */}
      <div className={cls.status}>
        {loading ? <LinearProgress sx={{ flex: 1 }} /> : <EligibilityStatusLine view={view} />}
      </div>

      <Collapse in={expanded} unmountOnExit>
        <div className={cls.detail}>
          <CoverageDetails
            lines={money}
            eligibility={!loading ? view : null}
            vehicleLabel={vehicleLabel}
            distanceMiles={distanceMiles}
          />
        </div>
      </Collapse>
    </Paper>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = () => ({
  bar: css`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    cursor: pointer;
  `,
  headline: css`
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-left: auto;
  `,
  status: css`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px 8px;
    padding: 0 16px 10px;
  `,
  detail: css`
    padding: 0 16px 16px;
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default MovePlannerProgramEligibility;
